import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import Button from "../../../../shared/ui/button/Button";
import Checkbox from "../../../../shared/ui/form/input/Checkbox";
import { Center, SessionMode, useUpdateCenter } from "../../../center";

const MODES: SessionMode[] = ["individual", "couple", "group"];

const MODE_FIELD = {
    individual: "individual_sessions_enabled",
    couple: "couple_sessions_enabled",
    group: "group_sessions_enabled",
} as const;

/**
 * How the centre's sessions are created: whether the clinical fields (therapy
 * type, template) are offered, and which kinds of session it runs at all. A
 * kind switched off disappears from every list, search and form.
 */
export default function CenterSessionPreferencesCard({ center }: { center: Center }) {
    const { t } = useTranslation();
    const update = useUpdateCenter(center.id);
    const training = center.type === "training";

    const [therapyFields, setTherapyFields] = useState(center.therapy_fields_enabled);
    const [modes, setModes] = useState<Record<SessionMode, boolean>>(() => modesOf(center));
    const [error, setError] = useState<string | null>(null);
    const [saved, setSaved] = useState(false);

    useEffect(() => {
        setTherapyFields(center.therapy_fields_enabled);
        setModes(modesOf(center));
    }, [center]);

    const save = async () => {
        setSaved(false);
        if (!MODES.some((mode) => modes[mode])) {
            setError(t("center.sessionPreferences.modesRequired"));
            return;
        }
        setError(null);
        try {
            await update.mutateAsync({
                therapy_fields_enabled: therapyFields,
                individual_sessions_enabled: modes.individual,
                couple_sessions_enabled: modes.couple,
                group_sessions_enabled: modes.group,
            });
            setSaved(true);
        } catch {
            setError(t("center.sessionPreferences.saveError"));
        }
    };

    return (
        <section
            aria-labelledby="session-preferences-title"
            className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]"
        >
            <h2 id="session-preferences-title" className="mb-1 text-lg font-semibold text-content-primary">
                {t(training ? "center.sessionPreferences.titleTraining" : "center.sessionPreferences.title")}
            </h2>
            <p className="mb-5 text-sm text-gray-500 dark:text-gray-400">
                {t(
                    training
                        ? "center.sessionPreferences.subtitleTraining"
                        : "center.sessionPreferences.subtitle"
                )}
            </p>

            <Checkbox
                id="center-therapy-fields"
                checked={therapyFields}
                onChange={setTherapyFields}
                label={t(
                    training
                        ? "center.sessionPreferences.therapyFieldsTraining"
                        : "center.sessionPreferences.therapyFields"
                )}
            />
            <p className="mt-1 pl-8 text-xs text-gray-500 dark:text-gray-400">
                {t("center.sessionPreferences.therapyFieldsHint")}
            </p>

            <fieldset className="mt-6">
                <legend className="mb-1 text-sm font-medium text-content-primary">
                    {t(
                        training
                            ? "center.sessionPreferences.modesTitleTraining"
                            : "center.sessionPreferences.modesTitle"
                    )}
                </legend>
                <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
                    {t("center.sessionPreferences.modesHint")}
                </p>
                <div className="flex flex-wrap gap-6">
                    {MODES.map((mode) => (
                        <Checkbox
                            key={mode}
                            id={`center-mode-${mode}`}
                            checked={modes[mode]}
                            onChange={(checked) => setModes((prev) => ({ ...prev, [mode]: checked }))}
                            label={t(`center.sessionPreferences.modes.${mode}`)}
                        />
                    ))}
                </div>
            </fieldset>

            {error && (
                <p role="alert" className="mt-4 text-sm text-red-600 dark:text-red-400">
                    {error}
                </p>
            )}
            {saved && !error && (
                <p role="status" className="mt-4 text-sm text-green-600 dark:text-green-400">
                    {t("center.sessionPreferences.saved")}
                </p>
            )}

            <div className="mt-5">
                <Button onClick={save} loading={update.isPending}>
                    {t("common.save")}
                </Button>
            </div>
        </section>
    );
}

function modesOf(center: Center): Record<SessionMode, boolean> {
    return {
        individual: center[MODE_FIELD.individual],
        couple: center[MODE_FIELD.couple],
        group: center[MODE_FIELD.group],
    };
}
